import { LitElement, html } from "lit";
import { render } from "lit/html.js";

export class IntervalDial extends LitElement {
  static properties = {
    value: { type: Number },
    min: { type: Number },
    max: { type: Number },
    step: { type: Number },
    open: { type: Boolean, reflect: true },
  };

  constructor() {
    super();
    this.value = 5;
    this.min = 1;
    this.max = 30;
    this.step = 1;
    this.open = false;

    this._angle = 0;
    this._dragging = false;

    this._portal = document.createElement("div");
    const shadow = this._portal.attachShadow({ mode: "open" });
    this._portalRoot = document.createElement("div");
    shadow.appendChild(this._portalRoot);

    this._portal.style.position = "fixed";
    this._portal.style.inset = "0";
    this._portal.style.pointerEvents = "none";
    this._portal.style.zIndex = "9999";
  }

  connectedCallback() {
    super.connectedCallback();
    document.body.appendChild(this._portal);
    this._renderToPortal();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._portal.remove();
  }

  updated() {
    this._renderToPortal();
  }

  _normalizedFromValue() {
    const range = this.max - this.min || 1;
    return (this.value - this.min) / range;
  }

  _renderDialSvg() {
    const pct = this._normalizedFromValue();
    const radius = 100;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference * (1 - pct);

    const angle = pct * 360 + 90; // 0–360, 0 at 12 o’clock (after -90° group)

    return html`
      <svg class="dial-svg" width="240" height="240" viewBox="0 0 240 240">
        <defs>
          <filter id="md-glow">
            <feDropShadow
              dx="0"
              dy="0"
              stdDeviation="6"
              flood-color="#4f8cff"
              flood-opacity="0.9"
            />
          </filter>
        </defs>

        <!-- Rotate -90 so 0% is at 12 o’clock -->
        <g transform="rotate(-90 120 120)">
          <!-- Track -->
          <circle
            cx="120"
            cy="120"
            r="${radius}"
            stroke="rgba(255,255,255,0.08)"
            stroke-width="12"
            fill="none"
          ></circle>

          <!-- Progress arc -->
          <circle
            class="arc-fill"
            cx="120"
            cy="120"
            r="${radius}"
            stroke="#4f8cff"
            stroke-width="12"
            stroke-linecap="round"
            fill="none"
            stroke-dasharray="${circumference}"
            stroke-dashoffset="${offset}"
            filter="url(#md-glow)"
          ></circle>

          <!-- Tick indicator riding the same circle -->
          <g transform="rotate(${angle} 120 120)">
            <rect
              x="116"
              y="${120 - radius - 12}"
              width="12"
              height="24"
              rx="4"
              ry="4"
              fill="#4f8cff"
              filter="url(#md-glow)"
            ></rect>
          </g>
        </g>
      </svg>
    `;
  }

  _renderToPortal() {
    const template = html`
      <style>
                .overlay {
                  display: none;
                  opacity: 0;
                  transition: opacity 200ms ease;
                  position: fixed;
                  inset: 0;
                  background: rgba(0, 0, 0, 0.45);
                  pointer-events: none;
                  z-index: 9999;
                  justify-content: center;
                  align-items: center;
                }
                .overlay.open {
                  display: flex;
                  opacity: 1;
                  pointer-events: all;
                }

                .backdrop {
                  position: absolute;
                  inset: 0;
                  pointer-events: auto;
                }

                .center-wrap {
                  pointer-events: none;
                  display: flex;
                  flex-direction: column;
                  gap: 24px;
                  align-items: center;
                  justify-content: center;
                  width: 100%;
                  height: 100%;
                }

                .dial {
                  pointer-events: auto;
                  width: 240px;
                  height: 240px;
                  border-radius: 50%;
                  background: #1e1e1e;
                  border: 2px solid rgba(255, 255, 255, 0.06);
                  box-shadow:
                    0 4px 12px rgba(0, 0, 0, 0.4),
                    0 2px 4px rgba(0, 0, 0, 0.3);
                  position: relative;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  overflow: hidden;
                  touch-action: none;
                }

                .value {
                  position: absolute;
                  font-size: 32px;
                  font-weight: 500;
                  color: #ffffff;
                }

                .label {
                  position: absolute;
                  bottom: 28px;
                  font-size: 13px;
                  color: #9bb0ff;
                  text-transform: uppercase;
                }

                .dial-svg {
                  position: absolute;
                  top: 0;
                  left: 0;
                  pointer-events: none;
                }

        /*        .arc-fill {
                  transition: stroke-dashoffset 200ms ease-out;
                }
                  *
      </style>

      <div class="overlay ${this.open ? "open" : ""}">
        <div class="backdrop" @click=${() => this._onBackdropClick()}></div>

        <div class="center-wrap">
        <wa-card>
        
  <h3 slot="header">Playback speed</h3>
          <div class="dial">
            ${this._renderDialSvg()}
            <div class="value">${this.value}s</div>
            <!-- div class="label">Slide interval</div -->
          </div>
          <wa-button
          slot="footer"
            circle
            @click=${() => this._onBackdropClick()}
            title="Close"
            size="sm"
            variant="ghost"
          >
            <wa-icon name="close"></wa-icon>
            Close
          </wa-button>
        </div>
        </wa-card>
      </div>
    `;

    render(template, this._portalRoot);

    this._dial = this._portalRoot.querySelector(".dial");
    if (this._dial) {
      this._bindEvents();
      this._syncAngleFromValue();
    }
  }

  _onBackdropClick() {
    this._snapToNearest();

    this.dispatchEvent(
      new CustomEvent("close-dial", {
        bubbles: true,
        composed: true,
      }),
    );
  }

  _getAngleFromPointer(e) {
    const rect = this._dial.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;

    const p = e.touches ? e.touches[0] : e;
    const dx = p.clientX - cx;
    const dy = p.clientY - cy;

    // 0° at 12 o’clock, clockwise
    return Math.atan2(dy, dx) * (180 / Math.PI) + 90;
  }

  _bindEvents() {
    if (this._startHandler) {
      this._dial.removeEventListener("mousedown", this._startHandler);
      this._dial.removeEventListener("touchstart", this._startHandler);
    }

    this._startHandler = (e) => this._onStart(e);

    this._dial.addEventListener("mousedown", this._startHandler);
    this._dial.addEventListener("touchstart", this._startHandler, {
      passive: false,
    });
  }

  _onStart(e) {
    e.preventDefault();
    this._dragging = true;

    this._startAngle = this._getAngleFromPointer(e);
    this._baseAngle = this._angle;

    this._moveHandler = (ev) => this._onMove(ev);
    this._endHandler = (ev) => this._onEnd(ev);

    window.addEventListener("mousemove", this._moveHandler);
    window.addEventListener("touchmove", this._moveHandler, { passive: false });
    window.addEventListener("mouseup", this._endHandler);
    window.addEventListener("touchend", this._endHandler);
  }

  _onMove(e) {
    if (!this._dragging) return;
    e.preventDefault();

    const current = this._getAngleFromPointer(e);
    const delta = current - this._startAngle;

    this._angle = this._baseAngle + delta;
    this._updateValueFromAngle(false);
    this.requestUpdate("value");
  }

  _onEnd(e) {
    if (!this._dragging) return;
    e && e.preventDefault();

    this._dragging = false;
    this._snapToNearest();

    window.removeEventListener("mousemove", this._moveHandler);
    window.removeEventListener("touchmove", this._moveHandler);
    window.removeEventListener("mouseup", this._endHandler);
    window.removeEventListener("touchend", this._endHandler);
  }

  _syncAngleFromValue() {
    const pct = this._normalizedFromValue();
    this._angle = pct * 360;
  }

  _updateValueFromAngle(dispatch = true) {
    const range = this.max - this.min || 1;

    let normalized = (this._angle / 360) % 1;
    if (normalized < 0) normalized += 1;

    const raw = this.min + normalized * range;
    const stepped = Math.round(raw / this.step) * this.step;
    const clamped = Math.max(this.min, Math.min(this.max, stepped));

    this.value = Number(clamped);

    if (dispatch) {
      this.dispatchEvent(
        new CustomEvent("change", {
          detail: this.value,
          bubbles: true,
          composed: true,
        }),
      );
    }
  }

  _snapToNearest() {
    const range = this.max - this.min || 1;

    let normalized = (this._angle / 360) % 1;
    if (normalized < 0) normalized += 1;

    const raw = this.min + normalized * range;
    const stepped = Math.round(raw / this.step) * this.step;
    const clamped = Math.max(this.min, Math.min(this.max, stepped));

    this.value = Number(clamped);

    this._syncAngleFromValue();
    this.requestUpdate("value");

    this.dispatchEvent(
      new CustomEvent("change", {
        detail: this.value,
        bubbles: true,
        composed: true,
      }),
    );
  }

  render() {
    return html`<span style="display:none">${this.open}</span>`;
  }
}

customElements.define("interval-dial", IntervalDial);
